import { Link } from "react-router-dom";
import { ArrowRight, Facebook, Instagram } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { IMAGES, BRAND } from "@/lib/site";

const PILLARS = [
    {
        n: "01",
        title: "Appearances",
        copy: "Where Courtnie is showing up around the Twin Cities — panels, pop-ups, and community spots.",
    },
    {
        n: "02",
        title: "Community events",
        copy: "Celebrations, showcases, and gatherings that bring neighborhoods together.",
    },
    {
        n: "03",
        title: "Performances",
        copy: "Live music and art from the stages, basements, and venues that keep the scene alive.",
    },
    {
        n: "04",
        title: "Event coverage",
        copy: "Quick check-ins and full recaps — the moment, the people, the energy.",
    },
];

const GALLERY = [
    { img: IMAGES.concert, caption: "Live from the stage" },
    { img: IMAGES.dj, caption: "In the mix" },
    { img: IMAGES.bentoCommunity, caption: "Community moments" },
];

export default function Events() {
    return (
        <>
            <PageHeader
                eyebrow="Around the Cities"
                title="Events"
                description="Upcoming appearances, community events, performances, and event coverage across the Twin Cities."
            />

            <section className="pb-24 sm:pb-32">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal>
                        <p className="eyebrow">What we cover</p>
                    </Reveal>
                    <div className="mt-8 grid gap-px overflow-hidden border border-white/10 sm:grid-cols-2 lg:grid-cols-4" data-testid="events-pillars">
                        {PILLARS.map((p) => (
                            <div
                                key={p.n}
                                className="group bg-coal/50 p-7 transition-colors hover:bg-coal"
                            >
                                <p className="font-mono text-xs tracking-[0.24em] text-neon-pink">
                                    {p.n}
                                </p>
                                <h3 className="mt-3 font-display text-lg font-bold uppercase">
                                    {p.title}
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-fog">
                                    {p.copy}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="pb-24 sm:pb-32">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal>
                        <p className="eyebrow">From recent coverage</p>
                        <h2 className="mt-4 font-display text-3xl font-bold uppercase sm:text-4xl">
                            The energy we pull up to
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="events-gallery">
                        {GALLERY.map((g, i) => (
                            <Reveal key={g.caption} delay={0.06 * i}>
                                <figure className="group relative overflow-hidden border border-white/10 bg-coal">
                                    <img
                                        src={g.img}
                                        alt={g.caption}
                                        className="h-72 w-full object-cover opacity-80 transition-all duration-700 group-hover:scale-105 group-hover:opacity-100"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-transparent to-transparent" />
                                    <figcaption className="absolute bottom-0 left-0 right-0 flex items-center justify-between p-5">
                                        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-silver">
                                            {g.caption}
                                        </span>
                                        <span className="h-px w-8 bg-neon-pink transition-all group-hover:w-14" />
                                    </figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="border-t border-white/10 bg-coal/40 py-20 sm:py-24">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
                        <Reveal>
                            <p className="eyebrow">Stay in the loop</p>
                            <h2 className="mt-4 font-display text-3xl font-bold uppercase sm:text-4xl">
                                Dates are announced first on our socials
                            </h2>
                            <p className="mt-5 max-w-lg leading-relaxed text-fog sm:text-lg">
                                New appearances and coverage dates drop on
                                Facebook and Instagram before they land here.
                                Follow along so you never miss a check-in.
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div
                                data-testid="events-follow-card"
                                className="border border-white/10 bg-ink p-8"
                            >
                                <p className="font-script text-3xl text-neon-pink neon-pink-text">
                                    follow the movement!
                                </p>
                                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                                    <a
                                        data-testid="events-facebook-link"
                                        href={BRAND.facebookUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex flex-1 items-center justify-center gap-2 border border-white/15 px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.14em] text-silver transition-all hover:border-neon-pink hover:text-neon-pink"
                                    >
                                        <Facebook size={15} /> FB ·{" "}
                                        {BRAND.facebook}
                                    </a>
                                    <a
                                        data-testid="events-instagram-link"
                                        href={BRAND.instagramUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex flex-1 items-center justify-center gap-2 border border-white/15 px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.14em] text-silver transition-all hover:border-neon-pink hover:text-neon-pink"
                                    >
                                        <Instagram size={15} /> IG ·{" "}
                                        {BRAND.instagram}
                                    </a>
                                </div>
                                <Link
                                    to="/contact?type=Event%20coverage"
                                    data-testid="events-coverage-cta"
                                    className="group mt-4 flex items-center justify-center gap-2 bg-neon-pink px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.14em] text-ink transition-all hover:neon-box-pink"
                                >
                                    Hosting something? Invite CBJ out
                                    <ArrowRight
                                        size={15}
                                        className="transition-transform group-hover:translate-x-1"
                                    />
                                </Link>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </>
    );
}
